import { NodeList } from "../../../Utils/Collections/NodeList";
import { TileBase } from "../Ground Tiles/TileBase";
import { Pool } from "../../../Utils/Collections/Pool";
import { ViewCellMapCell } from "./ViewCellMapCell";
import { ViewCellMapRow } from "./ViewCellMapRow";
import { WrappedArray } from "../../../Utils/Collections/WrapperArray";
import { View } from "./View";
import { GameWorld } from "../GameWorld";
import { SolidTile } from "../Ground Tiles/SolidTile";
import { Node } from "../../../Utils/Collections/Node";
import { Rectangle } from "../../../Utils/2D Shapes/Rectangle";
import { FragileTile } from "../Ground Tiles/FragileTile";
import { TrampolineTile } from "../Ground Tiles/TrampolineTile";
import { eTileType } from "../Game Map/MapCell";
import { FanTile } from "../Ground Tiles/FanTile";
import { PathGenerator, PathEntry, MapRowWrapper } from "../Game Map/PathGenerator";



export class ViewCellMap {
    /**
     * (RO) Staticky pristup
     */
    static get instance(): ViewCellMap { return ViewCellMap._instance; }
    private static _instance: ViewCellMap;

    /**
     * Pooly s volnymi dlazdicemi pro zem [typ dlazdice]
     */
    private _freeTiles: Pool<TileBase>[];

    /**
     * Seznam dlazdice zeme, u kterych je potreba volat update()
     */
    private _actTiles: NodeList<TileBase>;

    /**
     * Pool s volnymi bunkami mapy
     */
    private _freeCells: Pool<ViewCellMapCell>;

    /**
     * Pool s volnymi radky
     */
    private _freeRows: Pool<ViewCellMapRow>;

    /**
     * Mapa
     */
    private _map: WrappedArray<ViewCellMapRow>;

    /**
     * Index pristi radky mapy ve vygenerovane ceste
     */
    private _mapNextRowId: number;

    /**
     * Pozice prvni radky mapy v hernim svete na ose Z
     */
    private _mapFirstRowWorldZ: number;

    /**
     * Seznam radek, u kterych se vola update()
     */
    private _actRows: NodeList<ViewCellMapRow>;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        ViewCellMap._instance = this;

        // Bunky
        this._freeCells = new Pool<ViewCellMapCell>(ViewCellMapCell, 50, true);

        // Radky
        this._freeRows = new Pool<ViewCellMapRow>(ViewCellMapRow, 14, true);

        // Dlazdice
        this._freeTiles = [
            new Pool<SolidTile>(SolidTile, 50),
            new Pool<FragileTile>(FragileTile, 10),
            new Pool<TrampolineTile>(TrampolineTile, 0),
            new Pool<FanTile>(FanTile, 5)
        ];
        this._actTiles = new NodeList<TileBase>();

        // Mapa
        this._map = new WrappedArray<ViewCellMapRow>();
        this._actRows = new NodeList<ViewCellMapRow>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        // Uvolnit radky mapy
        while (!this._map.empty) {
            let row = this._map.remove(true);

            if (row) {
                row.release();
                this._freeRows.returnItem(row);
            }
        }
        this._actRows.clear();

        // Naplnit mapu
        this._mapFirstRowWorldZ = View.instance.botZ;
        this._mapNextRowId = Math.floor(-this._mapFirstRowWorldZ / TileBase.SIZE);

        this.fillMap();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        // Uvolnit radky, ktere se dostali mimo view (dole)
        let viewZ = View.instance.botZ;

        while (this._mapFirstRowWorldZ - TileBase.SIZE > viewZ) {
            let row = this._map.first;

            if (row) {
                if (row.deactivate()) {
                    this._freeRows.returnItem(this._map.remove(false));
                    this._mapFirstRowWorldZ -= TileBase.SIZE;
                }
                else {
                    // Radku se nepodarilo uvolnit
                    break;
                }
            }
            else {
                this._map.remove(false);
                this._mapFirstRowWorldZ -= TileBase.SIZE;
            }
        }

        // Doplnit nove radky (nahore)
        this.refillMap(true);

        // Zpracovat aktivni dlazdice
        this._actTiles.forEach((tile: TileBase, node: Node<TileBase>) => {
            if (!tile.update()) {
                this._actTiles.removeByNode(node);
                tile.actTileListNode = undefined;
            }
        });

        // Zpracovat aktivni radky
        this._actRows.forEach((row: ViewCellMapRow, node: Node<ViewCellMapRow>) => {
            if (!row.update())
                this._actRows.removeByNode(node);
        });
    }

    //-------------------------------------------------------------------------
    /**
     * Naplni pole dlazdicemi, ktere koliduji se zadanym obdelnikem
     * @param colRect Kolizni obdelnik se souradnicemi v hernim svete
     * @param res Pole, ktere bude naplneno dlazdicemi
     * @returns Vraci pocet dlazdic v poli
     */
    //-------------------------------------------------------------------------
    getCollisionCells(colRect: Rectangle, res: ViewCellMapCell[]): number {
        let rowId1 = Math.floor((this._mapFirstRowWorldZ - colRect.y) / TileBase.SIZE);
        let rowId2 = Math.floor((this._mapFirstRowWorldZ - colRect.bottom) / TileBase.SIZE);

        let colId1 = Math.floor(colRect.x / TileBase.SIZE);
        let colId2 = Math.floor(colRect.right / TileBase.SIZE);

        let cellCnt = 0;

        while (rowId1 >= rowId2) {
            let row = this._map.getItemAtIndex(rowId1);

            if (row) {
                let colId = colId1;

                while (colId <= colId2) {
                    let cell = row.getCell(colId);

                    if (cell)
                        res[cellCnt++] = cell;

                    colId++;
                }
            }

            rowId1--;
        }

        return cellCnt;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    getRow(worldZ: number): ViewCellMapRow {
        return this._map.getItemAtIndex(Math.floor((this._mapFirstRowWorldZ - worldZ) / TileBase.SIZE));
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    getFreeCell(): ViewCellMapCell {
        return this._freeCells.getItem();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    releaseCell(cell: ViewCellMapCell): void {
        cell.release();
        this._freeCells.returnItem(cell);
    }

    //-------------------------------------------------------------------------
    /**
     * Vraci volnou/neaktivni dlazdice pro dany typ a verzi dlazdice
     * @param tileType 
     */
    //-------------------------------------------------------------------------
    getFreeTile(tileType: eTileType): TileBase {
        return this._freeTiles[tileType - 1].getItem();
    }

    //-------------------------------------------------------------------------
    /**
     * Uvolneni dlazdice
     * @param tile 
     */
    //-------------------------------------------------------------------------
    releaseTile(tile: TileBase): void {
        if (tile.actTileListNode)
            this.remTileFromActList(tile);

        tile.release();
        this._freeTiles[tile.type - 1].returnItem(tile);
    }

    //-------------------------------------------------------------------------
    /**
     * Prida dlazdici do seznamu aktivnich dlazdic
     * @param tile 
     */
    //-------------------------------------------------------------------------
    addTileToActList(tile: TileBase): void {
        tile.actTileListNode = this._actTiles.add(tile);
    }

    //-------------------------------------------------------------------------
    /**
     * Odstraneni dlazdice se seznamu aktivnich dlazdic
     * @param tile 
     */
    //-------------------------------------------------------------------------
    remTileFromActList(tile: TileBase): void {
        this._actTiles.removeByNode(tile.actTileListNode);
        tile.actTileListNode = undefined;
    }

    //-------------------------------------------------------------------------
    /**
     * Doplni radky do mapy tak, aby bylo pokryto cele view
     * @param animateRowAct True, pokud se ma animovat aktivace novych radek
     */
    //-------------------------------------------------------------------------
    private refillMap(animateRowAct: boolean): void {
        let viewZ = View.instance.topZ;
        let nextRowZ = GameWorld.Z - this._mapNextRowId * TileBase.SIZE;

        if (nextRowZ > viewZ) {
            while (nextRowZ > viewZ) {
                let rowData = PathGenerator.INSTANCE.getNextRow();
                let row: ViewCellMapRow;

                if (rowData) {
                    row = this._freeRows.getItem();

                    if (row.init(this._mapNextRowId, rowData, animateRowAct))
                        this._actRows.add(row);
                }

                this._map.add(row, true);
                this._mapNextRowId++;
                nextRowZ -= TileBase.SIZE;
            }
        }
    }

    //-------------------------------------------------------------------------
    /**
     * Naplni radky do mapy
     */
    //-------------------------------------------------------------------------
    private fillMap(): void {
        let viewZ = View.instance.topZ;
        let nextRowZ = this._mapFirstRowWorldZ;

        let pathGen = PathGenerator.INSTANCE;

        let pathEntryId = pathGen.getPathEntryId(this._mapNextRowId);
        let pathEntry: PathEntry;
        let pathEntryRowId: number;

        if (pathEntryId >= 0) {
            pathEntry = pathGen.getPathEntry(pathEntryId);
            pathEntryRowId = pathEntry.sectionData.rows.length - (this._mapNextRowId - pathEntry.sectionRowPos + 1);
        }

        while (nextRowZ > viewZ) {
            let rowData: MapRowWrapper;

            if (pathEntry && pathGen.getNextRowWorldId() > this._mapNextRowId) {
                rowData = pathGen.getRow(pathEntryId, pathEntryRowId);

                if (pathEntryRowId-- == 0) {
                    // Prejit na dalsi zaznam
                    if ((pathEntry = pathGen.getPathEntry(++pathEntryId)) != undefined)
                        pathEntryRowId = pathEntry.sectionData.rows.length - 1;
                }
            }
            else {
                rowData = pathGen.getNextRow();
            }

            let row: ViewCellMapRow;

            if (rowData) {
                row = this._freeRows.getItem();

                if (row.init(this._mapNextRowId, rowData, false))
                    this._actRows.add(row);
            }

            this._map.add(row, true);
            this._mapNextRowId++;
            nextRowZ -= TileBase.SIZE;
        }
    }
}